import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Switch, message, Radio, Space, Typography, Row, Col, Tooltip } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import './styles/datepicker.css';

const { Text } = Typography;

const { TextArea } = Input;
const { Option } = Select;

// Priority mapping: User-friendly labels to numeric values for sorting
const PRIORITY_OPTIONS = {
  LOW: 1,
  MEDIUM: 5,
  HIGH: 10,
};

// Helper function to convert numeric priority to label
const getPriorityLabel = (numericPriority) => {
  const reverseMap = Object.entries(PRIORITY_OPTIONS).reduce((acc, [label, value]) => {
    acc[value] = label;
    return acc;
  }, {});
  return reverseMap[numericPriority] || 'MEDIUM';
};

// Duration presets in hours (constant - same for all instances)
const DURATION_PRESETS = {
  '3hours': { label: '3 Hours', hours: 3 },
  '6hours': { label: '6 Hours', hours: 6 },
  '12hours': { label: '12 Hours', hours: 12 },
  '1day': { label: '1 Day', hours: 24 },
  '1week': { label: '1 Week', hours: 24 * 7 },
  '1month': { label: '1 Month', hours: 24 * 30 },
};

// Helper function to determine which duration preset matches the given hours
const getDurationPresetFromHours = (hours) => {
  // Find the preset that matches this hour count
  for (const [key, value] of Object.entries(DURATION_PRESETS)) {
    if (value.hours === hours) {
      return key;
    }
  }
  // Default to 1day if no exact match
  return '1day';
};

// Helper function to check if alert has expired
const isAlertExpired = (endDate) => {
  return moment().isAfter(moment(endDate));
};

// Helper function to calculate original duration in hours
const calculateOriginalDuration = (startDate, endDate) => {
  return moment(endDate).diff(moment(startDate), 'hours');
};

const AlertFormModal = ({ visible, onCancel, onSubmit, initialValues, loading }) => {
  const [form] = Form.useForm();
  const [duration, setDuration] = useState('1day');
  const [startDate, setStartDate] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [originalDurationHours, setOriginalDurationHours] = useState(0);

  // Calculate end date based on selected duration
  const calculateEndDate = useCallback((start, durationKey) => {
    const hours = DURATION_PRESETS[durationKey].hours;
    return moment(start).add(hours, 'hours');
  }, []);

  useEffect(() => {
    if (visible && initialValues) {
      // Editing mode - show actual dates
      const startMoment = moment(initialValues.startDate);
      const endMoment = moment(initialValues.endDate);
      setStartDate(startMoment);

      // Calculate which duration preset was used
      const hoursDiff = endMoment.diff(startMoment, 'hours');
      const durationKey = getDurationPresetFromHours(hoursDiff);

      // Store original duration and check if expired
      setOriginalDurationHours(hoursDiff);
      const expired = isAlertExpired(initialValues.endDate);
      setIsExpired(expired);

      form.setFieldsValue({
        ...initialValues,
        priority: getPriorityLabel(initialValues.priority),
        startDate: startMoment,
        endDate: endMoment,
        // If alert is expired, show Active toggle as OFF (even if DB says true)
        // User must turn it ON to restart
        active: expired ? false : initialValues.active,
      });
      setDuration(durationKey);
    } else if (visible && !initialValues) {
      // Creating mode - auto-set start date to now
      const now = moment();
      setStartDate(now);
      setIsExpired(false);
      setOriginalDurationHours(24); // 1 day default
      form.resetFields();
      form.setFieldsValue({
        type: 'WARNING',
        dismissible: true,
        priority: 'MEDIUM',
        active: true,
        startDate: now,
        endDate: calculateEndDate(now, '1day'),
      });
      setDuration('1day');
    }
  }, [visible, initialValues, form, calculateEndDate]);

  // Update end date when duration changes
  useEffect(() => {
    if (startDate) {
      const newEndDate = calculateEndDate(startDate, duration);
      form.setFieldsValue({
        endDate: newEndDate,
      });
    }
  }, [duration, startDate, form, calculateEndDate]);

  // Handle Active toggle change for expired alerts (reactivation logic)
  const handleActiveToggle = (checked) => {
    if (isExpired && checked) {
      // Alert is expired and user is toggling it ON - reactivate with new dates
      const now = moment();
      const newEndDate = now.clone().add(originalDurationHours, 'hours');
      const durationKey = getDurationPresetFromHours(originalDurationHours);

      // Update state first
      setStartDate(now);
      setDuration(durationKey);

      // Then update form - set all values together
      form.setFieldsValue({
        active: true,
        startDate: now,
        endDate: newEndDate,
      });
    } else {
      // Normal toggle - just set active status
      form.setFieldsValue({
        active: checked,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Ensure date values are properly formatted
      let startDateValue = values.startDate;
      let endDateValue = values.endDate;

      // Handle both moment objects and strings
      if (startDateValue && startDateValue.toISOString) {
        startDateValue = startDateValue.toISOString();
      }
      if (endDateValue && endDateValue.toISOString) {
        endDateValue = endDateValue.toISOString();
      }

      const formattedValues = {
        ...values,
        // Convert priority label to numeric value
        priority: PRIORITY_OPTIONS[values.priority],
        startDate: startDateValue,
        endDate: endDateValue,
      };

      await onSubmit(formattedValues);
      // Don't reset form here - let parent component close modal
      // When modal reopens, initialValues will repopulate the form
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <Modal
      title={initialValues ? 'Edit System Alert' : 'Create System Alert'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      okText={initialValues ? 'Update' : 'Create'}
    >
      {isExpired && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fff7e6',
          border: '1px solid #ffd666',
          borderRadius: '4px',
          color: '#ad6800',
          fontWeight: 500,
        }}>
          ⏰ This alert has expired. You can restart it by turning the Active toggle ON to show it again with a new date range.
        </div>
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="type"
          label="Alert Type"
          rules={[{ required: true, message: 'Please select alert type' }]}
        >
          <Select>
            <Option value="INFO">Info (Blue)</Option>
            <Option value="WARNING">Warning (Yellow/Orange)</Option>
            <Option value="ERROR">Error (Red)</Option>
            <Option value="SUCCESS">Success (Green)</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="message"
          label="Message"
          rules={[
            { required: true, message: 'Please enter message' },
            { max: 500, message: 'Message cannot exceed 500 characters' },
          ]}
        >
          <TextArea
            rows={3}
            placeholder="e.g., Scheduled maintenance on Sunday, Jan 15th from 2-4 AM EST"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Row gutter={[32, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="linkText"
              label="Link Button Text (Optional)"
              tooltip="Only shown if Link URL is provided"
            >
              <Input placeholder="Learn More" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="link" label="Link URL (Optional)">
              <Input placeholder="https://status.mediajel.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[32, 0]}>
          <Col xs={24} sm={6}>
            <Form.Item
              name="priority"
              label="Priority"
              tooltip="Higher priority alerts display first to users"
              rules={[{ required: true, message: 'Please select priority' }]}
            >
              <Select placeholder="Select priority level">
                <Option value="LOW">Low</Option>
                <Option value="MEDIUM">Medium</Option>
                <Option value="HIGH">High</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="dismissible"
              label="Dismissible"
              valuePropName="checked"
              tooltip="Allow users to dismiss this alert"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="active"
              label="Active"
              valuePropName="checked"
              tooltip={
                isExpired
                  ? 'This alert has expired. Turning it on will restart it with a new date range.'
                  : 'Only active alerts are displayed to users'
              }
            >
              <Switch onChange={handleActiveToggle} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="startDate" hidden>
          <DatePicker />
        </Form.Item>

        <Form.Item name="endDate" hidden>
          <DatePicker />
        </Form.Item>

        <Form.Item
          label="Alert Schedule"
          tooltip="Alert starts immediately when created and runs for your selected duration. Times shown in your local timezone."
        >
          <Row gutter={[32, 16]} style={{ width: '100%' }}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>Duration:</Text>
                <Radio.Group
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}
                >
                  <Radio.Button value="3hours" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '8px 12px' }}>3 Hours</Radio.Button>
                  <Radio.Button value="6hours" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '8px 12px' }}>6 Hours</Radio.Button>
                  <Radio.Button value="12hours" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '8px 12px' }}>12 Hours</Radio.Button>
                  <Radio.Button value="1day" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '8px 12px' }}>1 Day</Radio.Button>
                  <Radio.Button value="1week" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '8px 12px' }}>1 Week</Radio.Button>
                  <Radio.Button value="1month" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '8px 12px' }}>1 Month</Radio.Button>
                </Radio.Group>
              </Space>
            </Col>
            <Col xs={24} sm={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>When:</Text>
                <div style={{ padding: '8px 12px', backgroundColor: '#fafafa', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                  <Text>
                    {startDate ? (() => {
                      const endDate = calculateEndDate(startDate, duration);
                      const isSameDay = startDate.format('MMM D, YYYY') === endDate.format('MMM D, YYYY');

                      if (isSameDay) {
                        return (
                          <>
                            {startDate.format('MMM D, YYYY')} <br /> from <strong>{startDate.format('h:mm A')}</strong> to{' '}
                            <strong>{endDate.format('h:mm A')}</strong>
                          </>
                        );
                      } else {
                        return (
                          <>
                            {startDate.format('MMM D')} at <strong>{startDate.format('h:mm A')}</strong> <br /> to{' '}
                            {endDate.format('MMM D')} at <strong>{endDate.format('h:mm A')}</strong>
                          </>
                        );
                      }
                    })() : (
                      'Now'
                    )}
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

AlertFormModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  loading: PropTypes.bool,
};

export default AlertFormModal;
export { PRIORITY_OPTIONS, getPriorityLabel };
